import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect } from 'react';
import firebaseAuth from '~/config/firebase/auth';
import firebaseFirestore from '~/config/firebase/firestore';
import { signOutAndClear } from '~/utils/session';
import { useAuth } from './useAuth';

/**
 * Cierra la sesión al instante cuando un superadmin la revoca desde el panel.
 *
 * Firebase invalida el token de refresco, pero el que ya está en el navegador
 * seguiría sirviendo hasta una hora, así que esperar no es una opción. En su
 * lugar se vigila `users/{uid}.sessionsRevokedAt` en tiempo real: si esa fecha
 * es posterior al momento en que se inició la sesión actual, se cierra y se
 * limpia el navegador.
 */
export const useSessionGuard = (): void => {
  const { user } = useAuth();
  const uid = user?.uid;

  useEffect(() => {
    if (!uid) return;

    const unsubscribe = onSnapshot(
      doc(firebaseFirestore, 'users', uid),
      async (snapshot) => {
        const revokedAt = snapshot.data()?.sessionsRevokedAt;
        if (!revokedAt || typeof revokedAt.toDate !== 'function') return;

        const currentUser = firebaseAuth.currentUser;
        if (!currentUser) return;

        try {
          const token = await currentUser.getIdTokenResult();
          const startedAt = new Date(token.authTime).getTime();

          if (revokedAt.toDate().getTime() > startedAt) {
            await signOutAndClear();
          }
        } catch {
          /* si el token ya no es legible, la sesión no sirve de nada */
          await signOutAndClear();
        }
      },
      () => {
        /* sin permisos o sin red: no cerramos la sesión por las dudas */
      }
    );

    return () => unsubscribe();
  }, [uid]);
};

export default useSessionGuard;
